import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ShowAlert } from '@app/core/actions/alert';
import {
  HIDE_LOADER_HEADER,
  HIDE_LOADER_HEADER_VALUE,
  InterceptorHttpParams
} from '@app/core/interceptors/interceptor-http-params';
import { AlertMessageType } from '@app/core/models/alert/alert-message-type';
import * as fromRoot from '@app/reducer';
import { Store } from '@ngrx/store';
import { containsMoreThanOneElement, isSingleton } from '@util';
import { Apollo } from 'apollo-angular';
import { DocumentNode, OperationDefinitionNode } from 'graphql';
import { FieldNode, SelectionNode } from 'graphql/language/ast';
import * as R from 'ramda';
import { head, prop, props } from 'ramda';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const splitErrorMessage = R.split('$');
const splitErrorMessageParams = R.split('|');
const hideLoader = R.path(['interceptorConfig', 'hideLoader']);
const mapResponse = R.pathOr(true, ['interceptorConfig', 'mapResponse']);

@Injectable()
export class GraphqlService {
  private static readonly ERROR_POLICY = 'all';
  private static readonly FETCH_POLICY = 'no-cache';

  private showWarnings = (result) => {
    if (result.errors) {
      const errorMessagesParts = splitErrorMessage(result.errors[0].message);

      const alert = {
        type: AlertMessageType.Warning,
        message: errorMessagesParts[0],
        params: splitErrorMessageParams(errorMessagesParts[1])
      };

      this.store.dispatch(new ShowAlert(alert));
    }
    return result;
  };

  private isFieldNode = (node: SelectionNode): node is FieldNode =>
    node.kind === 'Field';

  constructor(private apollo: Apollo, private store: Store<fromRoot.State>) {
  }

  mutation<T>(mutation: DocumentNode, variables: Record<string, any> = {}, params?: InterceptorHttpParams): Observable<T> {
    const mutationRequest = this.apollo.mutate<T>({
      mutation,
      errorPolicy: GraphqlService.ERROR_POLICY,
      fetchPolicy: GraphqlService.FETCH_POLICY,
      variables,
      context: this.contextFromParams(params)
    });

    return mutationRequest.pipe(
      map(this.showWarnings),
      map(R.prop('data')),
      map(result => mapResponse(params) ? this.toResponse(result, this.selections(mutation)) : result)
    );
  }

  query<T>(query: DocumentNode, variables?: Record<string, any>, params?: InterceptorHttpParams): Observable<T>;
  query<T, R>(query: DocumentNode, variables?: Record<string, any>, params?: InterceptorHttpParams): Observable<[T, R]>;
  query<T, R, V>(query: DocumentNode, variables?: Record<string, any>, params?: InterceptorHttpParams): Observable<[T, R, V]>;
  query<T, R, V>(query: DocumentNode, variables: Record<string, any> = {}, params?: InterceptorHttpParams): Observable<T | [T, R, V]> {
    return this.apollo.query<T>({
      query,
      errorPolicy: GraphqlService.ERROR_POLICY,
      fetchPolicy: GraphqlService.FETCH_POLICY,
      variables,
      context: this.contextFromParams(params)
    }).pipe(
      map(R.prop('data')),
      map(result => mapResponse(params) ? this.toResponse(result, this.selections(query)) : result)
    );
  }

  private contextFromParams(params?: InterceptorHttpParams): Record<string, any> {
    return hideLoader(params) === true
      ? {
        headers: new HttpHeaders().set(HIDE_LOADER_HEADER, HIDE_LOADER_HEADER_VALUE)
      }
      : {};
  }

  private toResponse(result: any, sectionNames: string[]): any {
    if (isSingleton(sectionNames)) {
      return prop(head(sectionNames), result);
    }

    if (containsMoreThanOneElement(sectionNames)) {
      return props(sectionNames, result);
    }

    return result;
  }

  private selections(documentNode: DocumentNode): string[] {
    const definition = head(documentNode.definitions) as OperationDefinitionNode;
    const fieldNodes: any[] = R.filter(this.isFieldNode, definition.selectionSet.selections);
    return fieldNodes.map(node => node.name.value);
  }

}
